import savingsSVG from '../assets/SBAB-savings-icon.svg'
import { AppState } from '../state/appState'
import SlideOver from './SlideOver'
import { Link, Text } from '@sbab-ui/components'
import { numberToStringWithSpaces } from '../lib/utils'

interface Props {
  appState: AppState
}

export default function Result({ appState }: Readonly<Props>) {
  function toCurrency(number: number) {
    const formatter = new Intl.NumberFormat('sv-SE', {
      style: 'decimal',
      currency: 'SEK',
    })
    return formatter.format(number)
  }

  return (
    <div className="mb-10 max-w-[400px] sm:max-w-[592px]">
      <div className="absolute flex h-28 w-[calc(100%-32px)] max-w-[678px] flex-row-reverse sm:h-auto sm:w-[calc(100%-48px)]">
        <img
          src={savingsSVG}
          alt="Slantar som trillar ner i en sparbössa"
          className="px-6 py-3 sm:p-0"
        />
      </div>
      <div data-cs-mask className="relative z-10 flex min-h-[129px] flex-col justify-center">
        <span
          className="mb-4 text-[45px] font-medium leading-[56px] text-black min-[400px]:text-[58px] min-[400px]:leading-[72px] sm:text-7xl sm:leading-[84px]"
          data-cy="result"
          data-nosnippet
        >
          {appState.hasCalculationError ? 0 : toCurrency(appState.totalAmount)} kr
        </span>
        <SlideOver buttonText="Så har vi räknat" title="Så har vi räknat">
          <Text size="h2" as="h3" className="text-black" mb="2x">
            Ränteberäkning
          </Text>
          {appState.hasCalculationError ? null : (
            <div data-cs-mask>
              <Text size="lead" as="p" className="text-black" mb="2x">
                Så här är uträkningen fördelad
              </Text>
              <Text size="paragraph" as="p" className="text-black">
                Startbelopp: {numberToStringWithSpaces(appState.startAmount)} kr
              </Text>
              <Text size="paragraph" as="p" className="text-black">
                Totalt månadssparande: {numberToStringWithSpaces(appState.accumulatedDeposited)} kr
              </Text>
              <Text size="paragraph" as="p" className="text-black" mb="2x">
                Avkastning (ränta): {numberToStringWithSpaces(appState.accumulatedInterest)} kr
              </Text>
              <Text size="paragraph" as="p" className="text-black" mb="2x">
                Vilket ger ett totalbelopp på {numberToStringWithSpaces(appState.totalAmount)} kr
                efter {appState.savingTimeInYears} år.
              </Text>
              <Text size="lead" as="p" className="text-black" mb="2x">
                Resultatet i den här uträkningen ska ses som ett räkneexempel och inte som en
                prognos eller garanti.
              </Text>
            </div>
          )}
          <Text size="paragraph" as="p" className="text-black" mb="2x">
            Vi har räknat med att räntan på sparkontot är{' '}
            <span className="font-semibold">
              {appState.interestRate.toFixed(2).toString().replace('.', ',')} %
            </span>
            . Om räntan ändras under perioden för sparandet kommer även den slutgiltiga summan att
            förändras. Aktuella räntesatser går alltid att se på{' '}
            <Link
              href="/1/privat/vara_rantor/vara_sparrantor"
              className="w-fit !text-link focus:outline-none focus:ring focus:ring-focus_blue"
            >
              sbab.se
            </Link>
            . I uträkningen har vi inte tagit hänsyn till skatter, avgifter eller inflation.
          </Text>
          <Text size="paragraph" as="p" className="text-black" mb="2x">
            Ränta räknas för samtliga kalenderdagar under året, alltså 365 dagar. Under skottår
            räknas ränta 366 dagar.
          </Text>
          <Text size="paragraph" as="p" className="text-black" mb="2x">
            Om pengarna sätts in från ett externt konto beräknas räntan från och med kalenderdagen
            efter att pengarna satts in. Sätts pengarna in från ett annat konto hos SBAB beräknas
            räntan från och med insättningsdagen. Ränta slutar att beräknas kalenderdagen innan
            pengarna tas ut.
          </Text>
          <Text size="paragraph" as="p" className="text-black" mb="2x">
            Upplupen ränta betalas ut vid årsskiftet eller vid kontoavslut. Om räntan är högre än
            100 kr per kontohavare dras 30 % i skatt. Vid redovisning till Skatteverket fördelas
            räntan lika mellan kontohavarna.
          </Text>
        </SlideOver>
      </div>
    </div>
  )
}
